import {
  Box,
  HStack,
  IconButton,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useBreakpointValue,
  useDisclosure,
} from "@chakra-ui/react";
import PampaImage from "@raiden/library/components/PampaImage";
import useTranslate from "@raiden/library/hooks/useTranslate";
import generateFrontPath from "@raiden/library/libraries/utils/generateFrontPath";
import { useIntl } from "react-intl";
import { CenteredContent } from "../CenteredContent";
import { Icon } from "../Icon";

/**
 * @callback childrenRender
 * @param {{disclosure: import("@chakra-ui/react").UseDisclosureReturn}} disclosure
 */
/**
 * @typedef {Object} Props
 * @property {import("@raiden/library/types/Cover").Cover[]} covers
 * @property {boolean} [isInModal]
 * @property {number} currentCover
 * @property {() => void} [nextCover]
 * @property {() => void} [previousCover]
 * @property {childrenRender} children
 */
/**
 * @param {Props} props
 */
export function ModalToSeeCovers({
  covers,
  isInModal = false,
  currentCover,
  nextCover,
  previousCover,
  children,
}) {
  const translate = useTranslate();

  const intl = useIntl();

  const disclosure = useDisclosure();

  const { isOpen, onClose } = disclosure;

  const format =
    useBreakpointValue({ base: "default", lg: "large" }) ?? "default";

  return (
    <>
      {covers && covers.length > 0 && (
        <Modal
          isOpen={isOpen}
          onClose={onClose}
          size="5xl"
          blockScrollOnMount={!isInModal}
          isCentered>
          <ModalOverlay />

          <ModalContent>
            <ModalHeader>
              <ModalCloseButton />
            </ModalHeader>

            <CenteredContent>
              <Box py=".5rem" px=".25rem">
                <PampaImage
                  image={covers[currentCover]}
                  alt={translate(covers?.[currentCover]?.caption) ?? ""}
                  format={format}
                  borderRadius="4px"
                  mode="crop"
                  placeholderSrc={generateFrontPath({
                    id: "@front.internal-assets",
                    parameters: { filePath: "placeholder.svg" },
                    includeBasePath: true,
                  })}
                  _groupHover={{
                    transform: "scale(1.1)",
                  }}
                  transition="all 0.25s ease"
                  bg="gray.100"
                />

                {covers && covers.length > 1 && (previousCover || nextCover) && (
                  <HStack
                    position="absolute"
                    top="38%"
                    left="0"
                    right="0"
                    justifyContent="space-between">
                    {previousCover && (
                      <IconButton
                        variant="outline"
                        h="150px"
                        icon={
                          <Icon
                            icon="ms_chevron_left"
                            size="24px"
                            color="white"
                          />
                        }
                        aria-label={intl.formatMessage({
                          defaultMessage: "Précédent",
                        })}
                        _hover={{ bg: "none" }}
                        onClick={previousCover}
                        isDisabled={currentCover === 0}
                      />
                    )}

                    {nextCover && (
                      <IconButton
                        variant="outline"
                        h="150px"
                        aria-label={intl.formatMessage({
                          defaultMessage: "Suivant",
                        })}
                        icon={
                          <Icon
                            icon="ms_chevron_right"
                            size="24px"
                            color="white"
                          />
                        }
                        _hover={{ bg: "none" }}
                        onClick={nextCover}
                        isDisabled={currentCover === covers.length - 1}
                      />
                    )}
                  </HStack>
                )}
              </Box>
            </CenteredContent>
          </ModalContent>
        </Modal>
      )}

      {children({ disclosure })}
    </>
  );
}
